import React from 'react';
import {
    Box,
    Modal,
    Typography,
    Select,
    MenuItem,
    RadioGroup,
    FormControlLabel,
    Radio,
    Stack,
    InputLabel,
    useMediaQuery,
    CircularProgress,
    IconButton,
    OutlinedInput,
    Checkbox,
    ListItemText
} from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useResponsiveToast } from '../../../components/Toast';
import { ActionButton } from '../../../components/Button';
import { logBookService } from '../../../services/logBookService';
import { locationService, Ward } from '../../../services/locationService';

interface ExportLogBookModalProps {
    open: boolean;
    onClose: () => void;
}

const ExportLogBookModal: React.FC<ExportLogBookModalProps> = ({ open, onClose }) => {
    const isMobile = useMediaQuery('(max-width:600px)');
    const toast = useResponsiveToast();

    // Status options
    const statusOptions = ['Pending', 'Review Passed', 'Approved', 'Review Failed', 'Rejected'];
    
    // Financial years (past 5 years + current year + 5 future years)
    const currentYear = new Date().getFullYear();
    const financialYears = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

    // State for form values
    const [selectedWard, setSelectedWard] = React.useState<string[]>([]);
    const [selectedYear, setSelectedYear] = React.useState<number[]>([]);
    const [selectedStatus, setSelectedStatus] = React.useState<string[]>([]);
    const [selectedFormat, setSelectedFormat] = React.useState<'pdf' | 'csv' | 'excel'>('pdf');
    const [isExporting, setIsExporting] = React.useState(false);
    
    // State for wards data
    const [wards, setWards] = React.useState<Ward[]>([]);
    const [wardsLoading, setWardsLoading] = React.useState(true);

    // Fetch wards data
    const fetchWards = async () => {
        try {
            setWardsLoading(true);
            const wardsData = await locationService.getWards();
            setWards(wardsData);
        } catch (err) {
            console.error('Error fetching wards:', err);
            toast.error('Failed to load wards data');
        } finally {
            setWardsLoading(false);
        }
    };

    // Load wards on modal open
    React.useEffect(() => {
        if (open) {
            fetchWards();
        }
    }, [open]);

    const style = {
        position: 'absolute' as const,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: '#fff',
        borderRadius: '32px',
        boxShadow: 24,
        px: isMobile ? 2 : 4,
        py: isMobile ? 3 : 5,
        width: isMobile ? '90%' : 500,
        maxWidth: isMobile ? '350px' : 500,
        maxHeight: isMobile ? '90vh' : '80vh',
        overflow: 'auto',
    };

    const selectBoxSx = {
        borderRadius: '12px',
        width: isMobile ? '100%' : 140,
        height: 48,
    };

    const handleExport = async () => {
        try {
            setIsExporting(true);
            
            await logBookService.exportLogBook({
                ward_number: selectedWard.length === 0 ? null : selectedWard,
                financial_year: selectedYear.length === 0 ? null : selectedYear,
                status: selectedStatus.length === 0 ? null : selectedStatus,
                format: selectedFormat
            });
            
            // Close modal after successful export
            onClose();
        } catch (error: any) {
            console.error('Export failed:', error);
            
            // Handle specific 404 error for no records found
            // Check if this is an AxiosError with response data (preserved by interceptor)
            if (error.response && error.response.data) {
                // For blob responses, we need to read the data as text to get the error message
                if (error.response.data instanceof Blob) {
                    try {
                        const text = await error.response.data.text();
                        const errorData = JSON.parse(text);
                        if (errorData.detail && errorData.detail.includes('No records found for export')) {
                            toast.error('No records found for the selected criteria. Please try different filters.');
                            return;
                        }
                    } catch (parseError) {
                        console.error('Error parsing blob response:', parseError);
                    }
                } else {
                    // For regular JSON responses
                    if (error.response.data.detail && error.response.data.detail.includes('No records found for export')) {
                        toast.error('No records found for the selected criteria. Please try different filters.');
                        return;
                    }
                }
            }
            
            // Handle other errors
            toast.error('Export failed. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={style}>
                <Stack spacing={isMobile ? 2 : 4}>
                    {/* Title */}
                    <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={1}>
                        <Stack direction='row' alignItems='center' spacing={1}>
                            <FileUploadOutlinedIcon sx={{ color: '#132F5F', fontSize: { xs: 20, sm: 28 } }} />
                            <Typography
                                fontFamily='Poppins, sans-serif'
                                fontWeight={600}
                                fontSize={{ xs: '16px', sm: '20px' }}
                                lineHeight='28px'
                                color='#132F5F'
                            >
                                Export Log Book Report
                            </Typography>
                        </Stack>
                        <IconButton onClick={onClose} sx={{ p: 0.5 }}>
                            <CloseIcon sx={{ color: '#132F5F', fontSize: 24 }} />
                        </IconButton>
                    </Stack>

                    {/* Filters */}
                    {isMobile ? (
                        <Stack spacing={1.5}>
                            {/* Row 1: Ward Number and Status */}
                            <Stack direction='row' spacing={1}>
                                <Box flex={1}>
                                    <InputLabel sx={{ fontSize: 11, fontFamily: 'Poppins, sans-serif', mb: 0.5 }}>
                                        Ward Number
                                    </InputLabel>
                                    <Select 
                                        multiple
                                        fullWidth 
                                        size='small' 
                                        value={selectedWard}
                                        onChange={(e) => setSelectedWard(e.target.value as string[])}
                                        input={<OutlinedInput />}
                                        renderValue={(selected) => {
                                            if (!selected || selected.length === 0) return 'All';
                                            return selected.join(', ');
                                        }}
                                        displayEmpty
                                        disabled={wardsLoading}
                                        sx={selectBoxSx}
                                    >
                                        {wards.map((ward) => (
                                            <MenuItem key={ward.id} value={ward.ward_number}>
                                                <Checkbox checked={selectedWard.indexOf(ward.ward_number) > -1} />
                                                <ListItemText primary={ward.ward_number} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                                <Box flex={1}>
                                    <InputLabel sx={{ fontSize: 11, fontFamily: 'Poppins, sans-serif', mb: 0.5 }}>
                                        Status
                                    </InputLabel>
                                    <Select 
                                        multiple
                                        fullWidth 
                                        size='small' 
                                        value={selectedStatus}
                                        onChange={(e) => setSelectedStatus(e.target.value as string[])}
                                        input={<OutlinedInput />}
                                        renderValue={(selected) => {
                                            if (!selected || selected.length === 0) return 'All';
                                            return selected.map(apiStatus => {
                                                // Map API status back to UI labels
                                                switch (apiStatus) {
                                                    case 'pending': return 'Pending';
                                                    case 'reviewed': return 'Review Passed';
                                                    case 'approved': return 'Approved';
                                                    case 'failed': return 'Review Failed';
                                                    case 'rejected': return 'Rejected';
                                                    default: return apiStatus;
                                                }
                                            }).join(', ');
                                        }}
                                        displayEmpty
                                        sx={selectBoxSx}
                                    >
                                        {statusOptions.map((status) => (
                                            <MenuItem key={status} value={logBookService.mapUIToAPIStatus(status)}>
                                                <Checkbox checked={selectedStatus.indexOf(logBookService.mapUIToAPIStatus(status)) > -1} />
                                                <ListItemText primary={status} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            </Stack>
                            
                            {/* Row 2: Financial Year (full width) */}
                            <Box>
                                <InputLabel sx={{ fontSize: 11, fontFamily: 'Poppins, sans-serif', mb: 0.5 }}>
                                    Financial Year
                                </InputLabel>
                                <Select 
                                    multiple
                                    fullWidth 
                                    size='small' 
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(e.target.value as number[])}
                                    input={<OutlinedInput />}
                                    renderValue={(selected) => {
                                        if (!selected || selected.length === 0) return 'All';
                                        return selected.join(', ');
                                    }}
                                    displayEmpty
                                    sx={selectBoxSx}
                                >
                                    {financialYears.map((year) => (
                                        <MenuItem key={year} value={year}>
                                            <Checkbox checked={selectedYear.indexOf(year) > -1} />
                                            <ListItemText primary={year.toString()} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                            
                        </Stack>
                    ) : (
                        <Stack direction='row' spacing={3} flexWrap='wrap'>
                            <Box>
                                <InputLabel sx={{ fontSize: 12, fontFamily: 'Poppins, sans-serif', mb: 0.5 }}>
                                    Ward Number
                                </InputLabel>
                                <Select 
                                    multiple
                                    fullWidth 
                                    size='small' 
                                    value={selectedWard}
                                    onChange={(e) => setSelectedWard(e.target.value as string[])}
                                    input={<OutlinedInput />}
                                    renderValue={(selected) => {
                                        if (!selected || selected.length === 0) return 'All';
                                        return selected.join(', ');
                                    }}
                                    displayEmpty
                                    disabled={wardsLoading}
                                    sx={selectBoxSx}
                                >
                                    {wards.map((ward) => (
                                        <MenuItem key={ward.id} value={ward.ward_number}>
                                            <Checkbox checked={selectedWard.indexOf(ward.ward_number) > -1} />
                                            <ListItemText primary={ward.ward_number} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                            <Box>
                                <InputLabel sx={{ fontSize: 12, fontFamily: 'Poppins, sans-serif', mb: 0.5 }}>
                                    Status
                                </InputLabel>
                                <Select 
                                    multiple
                                    fullWidth 
                                    size='small' 
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value as string[])}
                                    input={<OutlinedInput />}
                                    renderValue={(selected) => {
                                        if (!selected || selected.length === 0) return 'All';
                                        return selected.map(apiStatus => {
                                            // Map API status back to UI labels
                                            switch (apiStatus) {
                                                case 'pending': return 'Pending';
                                                case 'reviewed': return 'Review Passed';
                                                case 'approved': return 'Approved';
                                                case 'failed': return 'Review Failed';
                                                case 'rejected': return 'Rejected';
                                                default: return apiStatus;
                                            }
                                        }).join(', ');
                                    }}
                                    displayEmpty
                                    sx={selectBoxSx}
                                >
                                    {statusOptions.map((status) => (
                                        <MenuItem key={status} value={logBookService.mapUIToAPIStatus(status)}>
                                            <Checkbox checked={selectedStatus.indexOf(logBookService.mapUIToAPIStatus(status)) > -1} />
                                            <ListItemText primary={status} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                            <Box>
                                <InputLabel sx={{ fontSize: 12, fontFamily: 'Poppins, sans-serif', mb: 0.5 }}>
                                    Financial Year
                                </InputLabel>
                                <Select 
                                    multiple
                                    fullWidth 
                                    size='small' 
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(e.target.value as number[])}
                                    input={<OutlinedInput />}
                                    renderValue={(selected) => {
                                        if (!selected || selected.length === 0) return 'All';
                                        return selected.join(', ');
                                    }}
                                    displayEmpty
                                    sx={selectBoxSx}
                                >
                                    {financialYears.map((year) => (
                                        <MenuItem key={year} value={year}>
                                            <Checkbox checked={selectedYear.indexOf(year) > -1} />
                                            <ListItemText primary={year.toString()} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        </Stack>
                    )}

                    {/* Export Format */}
                    <Box>
                        <Typography
                            fontFamily='Poppins, sans-serif'
                            fontSize={{ xs: '12px', sm: '14px' }}
                            fontWeight={500}
                            sx={{ mb: 1, ml: 1 }}
                        >
                            Export Format
                        </Typography>
                        <RadioGroup 
                            value={selectedFormat}
                            onChange={(e) => setSelectedFormat(e.target.value as 'pdf' | 'csv' | 'excel')}
                        >
                            <Stack direction='row' spacing={isMobile ? 2 : 4} alignItems='center' ml={1}>
                                <FormControlLabel
                                    value='pdf'
                                    control={
                                        <Radio
                                            sx={{
                                                color: '#132F5F',
                                                '&.Mui-checked': { color: '#132F5F' },
                                            }}
                                        />
                                    }
                                    label='pdf'
                                    labelPlacement={isMobile ? 'end' : 'start'}
                                    sx={{ 
                                        fontFamily: 'Poppins, sans-serif', 
                                        ml: isMobile ? 0 : 1,
                                        fontSize: { xs: 12, sm: 14 }
                                    }}
                                />
                                <FormControlLabel
                                    value='excel'
                                    control={
                                        <Radio
                                            sx={{
                                                color: '#132F5F',
                                                '&.Mui-checked': { color: '#132F5F' },
                                            }}
                                        />
                                    }
                                    label='excel'
                                    labelPlacement={isMobile ? 'end' : 'start'}
                                    sx={{ 
                                        fontFamily: 'Poppins, sans-serif',
                                        fontSize: { xs: 12, sm: 14 }
                                    }}
                                />
                                <FormControlLabel
                                    value='csv'
                                    control={
                                        <Radio
                                            sx={{
                                                color: '#132F5F',
                                                '&.Mui-checked': { color: '#132F5F' },
                                            }}
                                        />
                                    }
                                    label='csv'
                                    labelPlacement={isMobile ? 'end' : 'start'}
                                    sx={{ 
                                        fontFamily: 'Poppins, sans-serif',
                                        fontSize: { xs: 12, sm: 14 }
                                    }}
                                />
                            </Stack>
                        </RadioGroup>
                    </Box>

                    {/* Export Button */}
                    <Box display='flex' justifyContent='center' mt={1}>
                        <ActionButton
                            startIcon={
                                isExporting ? (
                                    <CircularProgress size={isMobile ? 16 : 20} color="inherit" />
                                ) : (
                                    <FileUploadOutlinedIcon sx={{ fontSize: { xs: '14px', sm: '18px' } }} />
                                )
                            }
                            onClick={handleExport}
                            disabled={isExporting}
                            width="100%"
                            sx={{
                                height: { xs: 44, sm: 56 },
                                fontSize: { xs: 13, sm: 16 },
                                lineHeight: '24px',
                                fontWeight: 500,
                                px: { xs: 2, sm: 4 },
                                maxWidth: { sm: 128 },
                                '&:disabled': {
                                    backgroundColor: '#ccc',
                                },
                            }}
                        >
                            {isExporting ? 'Exporting...' : 'Export'}
                        </ActionButton>
                    </Box>
                </Stack>
            </Box>
        </Modal>
    );
};

export default ExportLogBookModal; 